import { useState } from "react";
import { Chip, Paper, Stack, Typography } from "@mui/material";
import { useNamesQuery } from "../hooks/useNames";
import { getRelationshipDisplayLabel } from "../utils/relationshipLabels";
import ProfileEdit from "./ProfileEdit";
import GoHomeButton from "./GoHomeButton";

export default function ProfilePicker({ userId, activeGroupId, isParent, onNavigateHome }) {
  const { data: names = [] } = useNamesQuery(activeGroupId);
  const [selected, setSelected] = useState(null);

  const childLists = [...names]
    .filter((n) => n.user_id === userId && n.is_child_list)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (selected != null) {
    return (
      <ProfileEdit
        userId={userId}
        childNameId={selected.childNameId}
        label={selected.label}
        onBack={() => setSelected(null)}
        onNavigateHome={onNavigateHome}
      />
    );
  }

  // "Your own profile" maps to the personal (child_name_id = null) row and is
  // always shown, whether or not the user has a personal list in this group.
  const cards = [
    { key: "self", title: "Your own profile", chip: null, selection: { childNameId: null, label: null } },
    ...childLists.map((n) => ({
      key: n.id,
      title: n.name,
      chip: getRelationshipDisplayLabel(n),
      selection: { childNameId: n.id, label: n.name },
    })),
  ];

  return (
    <Paper elevation={2} className="page-card" sx={{ p: 3, mt: 2, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" className="section-header">
          My Profile
        </Typography>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Share gift-relevant preferences so the rest of your group has ideas to work with
        when nothing on the wishlist quite fits. This is one profile about you, shown to
        every group you belong to. Your exact date of birth stays private — others only
        ever see an age range.
      </Typography>

      {isParent && childLists.length > 0 && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Pick whose profile to update — your own, or someone you manage a list for.
        </Typography>
      )}

      <Stack spacing={2}>
        {cards.map((card) => (
          <Paper
            key={card.key}
            variant="outlined"
            onClick={() => setSelected(card.selection)}
            sx={{
              p: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              transition: "transform 0.2s ease",
              "&:hover": { transform: "translateY(-1px)" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontWeight: 700, color: "royalblue" }}>{card.title}</Typography>
              {card.chip && <Chip label={card.chip} size="small" />}
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Edit profile →
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}
